import Image from 'next/image';
import type React from 'react';
import type { FC } from 'react';
import { CheckIcon as Check } from '@/components/icons/CheckIcon';
import { cn } from '@/utils/cn';
import type { PaymentOption, PaymentSpecifications } from './schemas';

interface PaymentDetailsProps {
  paymentOption: PaymentOption;
  className?: string;
}

interface PaymentDetailLineProps {
  details: PaymentSpecifications;
  type: 'Deposit' | 'Withdrawal';
}

export const PaymentDetailsTable: FC<PaymentDetailsProps> = ({
  paymentOption,
  className,
}) => (
  <div
    className={cn(
      'w-full gap-3 rounded-lg bg-component-bg bg-custom-gradient p-4',
      className
    )}
  >
    <div className="flex items-center">
      <Image
        alt="bookmaker-logo-payments-section-2"
        className="m-[14px] rounded-lg"
        height={32}
        src={paymentOption.logoUrl}
        width={32}
      />
      <span className="items-center font-medium text-xl">
        {paymentOption.name}
      </span>
    </div>
    <div className="grid gap-3">
      <PaymentDetailLine details={paymentOption.deposit} type="Deposit" />
      <PaymentDetailLine details={paymentOption.withdrawal} type="Withdrawal" />
    </div>
  </div>
);

const PaymentDetailLine: FC<PaymentDetailLineProps> = ({ details, type }) => (
  <div className="grid grid-cols-12 gap-3 rounded-lg bg-custom-gradient-2 p-3">
    <div className="col-span-12 flex items-center md:col-span-3">
      <Check
        className="m-[14px] block text-green"
        desc="Check mark icon for payment method"
        title="Payment Method"
      />
      <span className="ml-2 opacity-50">{type}</span>
    </div>
    <div className="col-span-12 grid grid-cols-4 gap-2 md:col-span-9 md:grid-cols-[1fr_auto_auto_auto]">
      <TableCell>
        <CellHeader>Max limit</CellHeader>
        <CellValue>{details.max}</CellValue>
      </TableCell>
      <TableCell>
        <CellHeader>Processing time</CellHeader>
        <CellValue>{details.processingTime}</CellValue>
      </TableCell>
      <TableCell>
        <CellHeader>Min limit</CellHeader>
        <CellValue>{details.min}</CellValue>
      </TableCell>
      <TableCell>
        <CellHeader>Transaction fee</CellHeader>
        <CellValue>{details.transactionFee}</CellValue>
      </TableCell>
    </div>
  </div>
);

const CellHeader: FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="flex items-center font-normal text-sm opacity-50">
    {children}
  </span>
);

const CellValue: FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="flex items-center font-semibold uppercase">{children}</span>
);

const TableCell: FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'col-span-2 ml-4 flex flex-col justify-around gap-1 md:col-span-1',
      className
    )}
  >
    {children}
  </div>
);
